import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const formatTime = value => {
  const seconds = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const playbackRates = [1, 1.25, 1.5, 2];
const playbackRateStorageKey = 'scriptureAudioPlaybackRate';
const playbackRateChangeEvent = 'scripture-audio-playback-rate-change';

const getSavedPlaybackRate = () => {
  try {
    const savedRate = Number(window.localStorage.getItem(playbackRateStorageKey));
    return playbackRates.includes(savedRate) ? savedRate : 1;
  } catch {
    return 1;
  }
};

const ResponsiveAudioPlayer = forwardRef(function ResponsiveAudioPlayer({
  className = '', onLoadedMetadata, onDurationChange, onTimeUpdate,
  onPlay, onPause, onEnded, onError, ...audioProps
}, forwardedRef) {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(getSavedPlaybackRate);

  useImperativeHandle(forwardedRef, () => audioRef.current);

  useEffect(() => {
    const applyPlaybackRate = rate => {
      if (!playbackRates.includes(rate)) return;
      setPlaybackRate(rate);
      if (audioRef.current) {
        audioRef.current.defaultPlaybackRate = rate;
        audioRef.current.playbackRate = rate;
      }
    };

    applyPlaybackRate(getSavedPlaybackRate());
    const handlePlaybackRateChange = event => applyPlaybackRate(Number(event.detail));
    window.addEventListener(playbackRateChangeEvent, handlePlaybackRateChange);
    return () => window.removeEventListener(playbackRateChangeEvent, handlePlaybackRateChange);
  }, []);

  const updateDuration = event => {
    setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0);
  };

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  const seekBy = seconds => {
    const audio = audioRef.current;
    if (!audio) return;
    const maximum = Number.isFinite(audio.duration) ? audio.duration : Infinity;
    audio.currentTime = Math.min(Math.max(audio.currentTime + seconds, 0), maximum);
    setCurrentTime(audio.currentTime);
  };

  const seekTo = event => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Number(event.target.value);
    setCurrentTime(audio.currentTime);
  };

  const cyclePlaybackRate = () => {
    const nextRate = playbackRates[(playbackRates.indexOf(playbackRate) + 1) % playbackRates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.defaultPlaybackRate = nextRate;
      audioRef.current.playbackRate = nextRate;
    }
    try {
      window.localStorage.setItem(playbackRateStorageKey, String(nextRate));
    } catch {}
    window.dispatchEvent(new CustomEvent(playbackRateChangeEvent, { detail: nextRate }));
  };

  return (
    <div className={`responsive-audio-player ${className}`.trim()}>
      <audio
        {...audioProps}
        ref={audioRef}
        className="responsive-audio-element"
        onLoadedMetadata={event => {
          updateDuration(event);
          setCurrentTime(event.currentTarget.currentTime || 0);
          event.currentTarget.defaultPlaybackRate = playbackRate;
          event.currentTarget.playbackRate = playbackRate;
          onLoadedMetadata?.(event);
          setCurrentTime(event.currentTarget.currentTime || 0);
        }}
        onDurationChange={event => { updateDuration(event); onDurationChange?.(event); }}
        onTimeUpdate={event => { setCurrentTime(event.currentTarget.currentTime || 0); onTimeUpdate?.(event); }}
        onPlay={event => {
          // Starting a scripture player also pauses audio behind an open modal.
          document.querySelectorAll('audio').forEach(otherAudio => {
            if (otherAudio !== event.currentTarget && !otherAudio.paused) otherAudio.pause();
          });
          setIsPlaying(true);
          onPlay?.(event);
        }}
        onPause={event => { setIsPlaying(false); onPause?.(event); }}
        onEnded={event => { setIsPlaying(false); onEnded?.(event); }}
        onError={onError}
      />
      <div className="responsive-audio-progress-row">
        <input
          className="responsive-audio-progress"
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={Math.min(currentTime, duration || 0)}
          disabled={!duration}
          onChange={seekTo}
          aria-label="播放進度"
          style={{ '--audio-progress': `${duration ? (currentTime / duration) * 100 : 0}%` }}
        />
        <div className="responsive-audio-times"><span>{formatTime(currentTime)}</span><span>{formatTime(duration)}</span></div>
      </div>
      <div className="responsive-audio-buttons">
        <button type="button" className="responsive-audio-skip" onClick={() => seekBy(-30)} aria-label="倒退三十秒"><span aria-hidden="true">↶</span><small>30</small></button>
        <button type="button" className="responsive-audio-play" onClick={togglePlayback} aria-label={isPlaying ? '暫停' : '播放'}><span aria-hidden="true">{isPlaying ? '❚❚' : '▶'}</span></button>
        <button type="button" className="responsive-audio-skip" onClick={() => seekBy(30)} aria-label="快進三十秒"><span aria-hidden="true">↷</span><small>30</small></button>
        <button type="button" className="responsive-audio-rate" onClick={cyclePlaybackRate} aria-label="切換播放速度">{playbackRate}x</button>
      </div>
    </div>
  );
});

export default ResponsiveAudioPlayer;
